// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { xCommonUpdate } from "@vanmeter/App/Handlers/XNG/Common";
import { fontConfig } from "@vanmeter/XDL/constants";
import IconAngleDownSmall from "@vanmeter/XDL/Icons/IconAngleDownSmall";
import IconAngleUpSmall from "@vanmeter/XDL/Icons/IconAngleUpSmall";
import { color, mergeWithCss, xClassNames, xScreen } from "@vanmeter/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface HeaderClickableItemProps {
    isOpen?: boolean;
    showArrow?: boolean;
    showIcon?: boolean;
    hideOnTablet?: boolean;
    icon?: IconProps;
    extendedStyles?: HeaderClickableStyles;
}

export type HeaderClickableProps = HeaderClickableItemProps & ClickableProps;

type Props = HeaderClickableProps;

export interface HeaderClickableStyles {
    clickable?: ClickablePresentationProps;
    icon?: IconProps;
    label?: TypographyPresentationProps;
    arrow?: IconProps;
}

export const headerClickableStyles: HeaderClickableStyles = {
    clickable: {
        css: css`
            display: flex;
            gap: 8px;
            align-items: center;
            justify-content: center;
            padding: 25px 16px;
            max-height: 68px;

            &:hover,
            &.opened {
                background-color: ${color("common.ui1")};
                svg,
                ${TypographyStyle} {
                    color: ${color("common.ui15")};
                }
            }

            ${xScreen("lte", "md")} {
                padding: 0;
                width: 30px;
                height: 30px;
                &:hover {
                    background-color: transparent;
                    svg,
                    ${TypographyStyle} {
                        color: ${color("common.ui17")};
                    }
                }
                ${({ hideOnTablet }: any) => hideOnTablet && `display:none;`}
            }
        `,
    },
    icon: {
        color: "common.ui1",
        size: 22,
    },
    label: {
        size: fontConfig.regular.size,
        lineHeight: fontConfig.smallSingleLine.lineHeight,
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.secondaryFontFamily,
        color: "common.white",
        css: css`
            display: flex;
            flex-direction: column;
            white-space: nowrap;
            &:empty {
                display: none;
            }
        `,
    },
    arrow: {
        width: 9,
        height: 13,
        color: "common.ui1",
    },
};

const HeaderClickable: FC<Props> = ({
    isOpen,
    showArrow = false,
    showIcon = true,
    icon,
    extendedStyles,
    children,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));
    const className = xClassNames.append([isOpen && "opened"], otherProps.className);
    const dispatch = useAppDispatch();
    const createAccountPageLinkUrl = useAppSelector(state => getPageLinkByPageType(state, "CreateAccountPage")?.url);

    const handleOnClick = () => {
        if (otherProps.href === createAccountPageLinkUrl) {
            dispatch(xCommonUpdate({ key: "isAccountCreated", value: false }));
            dispatch(xCommonUpdate({ key: "hasAnAccount", value: false }));
        }
    };
    return (
        <Clickable {...styles.clickable} {...otherProps} className={className} onClick={handleOnClick}>
            {showIcon && (styles.icon?.src || icon?.src) && (
                <Icon {...styles.icon} {...icon} className="clickable-icon" />
            )}
            <Typography {...styles.label}>{children}</Typography>
            {showArrow && <Icon {...styles.arrow} src={isOpen ? IconAngleUpSmall : IconAngleDownSmall} />}
        </Clickable>
    );
};

export default HeaderClickable;
